/*  CourseBuilder — Screen 3 of the Certification Author flow.
    ────────────────────────────────────────────────────────────
    Split view:
      • LEFT  — <ModuleListPanel />  (module CRUD + reorder + lock)
      • RIGHT — <LessonEditor />     (lesson list + per-type editor)

    Top bar shows course title, status pill, and actions:
      • Edit setup (goes to CourseSetup)
      • Submit for review (only when status='draft' or 'rejected')

    Route: /creator/courses/:courseId
*/
import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    ArrowLeft, PencilSimple, PaperPlaneTilt, Warning, ClockCounterClockwise,
    CheckCircle, XCircle, Info,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import ModuleListPanel, { FINAL_EXAM_ID } from "@/components/creator/ModuleListPanel";
import LessonEditor from "@/components/creator/LessonEditor";
import FinalExamBuilder from "@/components/creator/FinalExamBuilder";
import { confirmDialog } from "@/lib/confirm";


const STATUS_META = {
    draft:        { label: "Draft",        cls: "bg-slate-100 text-slate-700 border-slate-200",       Icon: PencilSimple },
    under_review: { label: "Under Review", cls: "bg-amber-50 text-amber-800 border-amber-200",         Icon: ClockCounterClockwise },
    published:    { label: "Published",    cls: "bg-emerald-50 text-emerald-800 border-emerald-200",   Icon: CheckCircle },
    rejected:     { label: "Rejected",     cls: "bg-rose-50 text-rose-800 border-rose-200",            Icon: XCircle },
};


export default function CourseBuilder() {
    const { courseId } = useParams();
    const navigate = useNavigate();

    const [course, setCourse] = useState(null);
    const [modules, setModules] = useState([]);
    const [activeModuleId, setActiveModuleId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/course-builder/courses/${courseId}`);
            const { modules: mods = [], ...rest } = data;
            setCourse(rest);
            setModules(mods);
            setActiveModuleId((prev) => {
                if (prev === FINAL_EXAM_ID && rest.has_final_exam) return prev;
                if (prev && mods.some((m) => m.id === prev)) return prev;
                return mods[0]?.id || null;
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load course.");
            navigate("/creator");
        } finally {
            setLoading(false);
        }
    }, [courseId, navigate]);

    useEffect(() => { load(); }, [load]);

    const readOnly = course?.status === "under_review" || course?.status === "published";
    const meta = STATUS_META[course?.status] || STATUS_META.draft;
    const activeModule = modules.find((m) => m.id === activeModuleId) || null;

    const onLessonsChange = useCallback((moduleId, lessons) => {
        setModules((prev) => prev.map((m) => (m.id === moduleId ? { ...m, lessons } : m)));
    }, []);

    const totalLessons = modules.reduce((acc, m) => acc + (m.lessons?.length || 0), 0);

    const onSubmit = async () => {
        // Client-side sanity gate before the network call.
        if (modules.length === 0) {
            toast.error("Add at least one module before submitting.");
            return;
        }
        if (totalLessons === 0) {
            toast.error("Add at least one lesson before submitting.");
            return;
        }
        const ok = await confirmDialog({
            title: "Submit for admin review?",
            description:
                "Once submitted, the course becomes read-only until the admin approves or requests changes. You can keep drafting other courses while this one is under review.",
            confirmLabel: "Submit for review",
        });
        if (!ok) return;
        try {
            setSubmitting(true);
            await api.post(`/course-builder/courses/${courseId}/submit`, { note: "" });
            toast.success("Submitted for admin review");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't submit.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading || !course) {
        return (
            <div className="p-8">
                <div className="h-9 w-40 bg-secondary rounded animate-pulse" />
                <div className="mt-4 h-96 bg-secondary rounded animate-pulse" />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-secondary/30" data-testid="course-builder">
            {/* Top bar */}
            <header className="bg-card border-b border-border" data-testid="course-builder-header">
                <div className="px-6 py-4 flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                        <Link
                            to="/creator"
                            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors no-underline"
                            data-testid="builder-back-link"
                        >
                            <ArrowLeft size={11} weight="bold" /> My Courses
                        </Link>
                        <div className="mt-2 flex items-center gap-3 flex-wrap">
                            <h1 className="font-display text-2xl font-bold tracking-tight truncate max-w-xl" data-testid="course-builder-title">
                                {course.title}
                            </h1>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${meta.cls}`} data-testid="course-builder-status">
                                <meta.Icon size={10} weight="fill" /> {meta.label}
                            </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                            {course.track && <span>Track: <span className="text-foreground">{course.track}</span></span>}
                            {course.phase && <span>Phase: <span className="text-foreground">{course.phase}</span></span>}
                            <span>{modules.length} module{modules.length === 1 ? "" : "s"}</span>
                            <span>{totalLessons} lesson{totalLessons === 1 ? "" : "s"}</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/creator/courses/${course.id}/setup`)}
                            data-testid="builder-edit-setup"
                        >
                            <PencilSimple size={12} weight="fill" className="mr-1" /> Edit setup
                        </Button>
                        {(course.status === "draft" || course.status === "rejected") && (
                            <Button
                                onClick={onSubmit}
                                disabled={submitting}
                                data-testid="builder-submit-btn"
                            >
                                <PaperPlaneTilt size={12} weight="fill" className="mr-1" />
                                {submitting ? "Submitting…" : "Submit for review"}
                            </Button>
                        )}
                    </div>
                </div>

                {/* Contextual banner */}
                {course.status === "rejected" && (
                    <RejectionBanner courseId={course.id} />
                )}
                {readOnly && (
                    <div className="px-6 py-2.5 bg-amber-50 border-t border-amber-200 flex items-center gap-2 text-xs text-amber-900" data-testid="builder-readonly-banner">
                        <Info size={13} weight="fill" className="text-amber-600" />
                        <span className="font-semibold">
                            {course.status === "under_review"
                                ? "This course is under admin review. Editing is disabled until a decision is made."
                                : "This course is published. Editing is disabled; ask an admin to unpublish to make changes."}
                        </span>
                    </div>
                )}
            </header>

            {/* Split view */}
            <div className="flex-1 flex flex-col lg:flex-row min-h-0">
                <ModuleListPanel
                    course={course}
                    modules={modules}
                    activeModuleId={activeModuleId}
                    onSelectModule={setActiveModuleId}
                    onModulesChange={setModules}
                    readOnly={readOnly}
                />
                {activeModuleId === FINAL_EXAM_ID ? (
                    <div className="flex-1 min-w-0 overflow-y-auto p-6">
                        <div className="max-w-3xl xl:max-w-4xl mx-auto">
                            <div className="mb-4">
                                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-rose-700">Course Capstone</div>
                                <h2 className="font-display text-xl font-bold mt-0.5">Final Exam</h2>
                                <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                                    Author the questions doctors will answer at the end of this course. Switch to auto-sample if you&apos;d rather pull questions from your module quizzes at random.
                                </p>
                            </div>
                            <FinalExamBuilder
                                courseId={course.id}
                                disabled={readOnly}
                                expectedCount={course.final_exam_question_count}
                                passScore={course.final_exam_pass_score}
                            />
                        </div>
                    </div>
                ) : (
                    <LessonEditor
                        course={course}
                        module={activeModule}
                        readOnly={readOnly}
                        onLessonsChange={onLessonsChange}
                    />
                )}
            </div>
        </div>
    );
}


function RejectionBanner({ courseId }) {
    const [review, setReview] = useState(null);

    useEffect(() => {
        let ok = true;
        api.get(`/course-builder/courses/${courseId}`)
            .then(({ data }) => {
                if (!ok) return;
                // Reviews aren't returned in the course tree — we'll surface
                // the feedback via a dedicated endpoint in Phase 3. For now,
                // just show a generic "changes requested" banner.
            })
            .catch(() => {});
        return () => { ok = false; };
    }, [courseId]);

    return (
        <div className="px-6 py-2.5 bg-rose-50 border-t border-rose-200 flex items-center gap-2 text-xs text-rose-900" data-testid="builder-rejection-banner">
            <Warning size={13} weight="fill" className="text-rose-600" />
            <span className="font-semibold">
                Changes requested by admin. Update the course and re-submit for review.
            </span>
        </div>
    );
}
